import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Loader2, ArrowRight } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROJECT_TYPES = [
  { value: "lvp", label: "LVP Flooring" },
  { value: "laminate", label: "Laminate Flooring" },
  { value: "tile", label: "Tile Installation" },
  { value: "other", label: "Other / Multiple" },
];

export default function QuoteContact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    project_type: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.project_type) {
      toast.error("Please fill in your name, email, and project type.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Quote request received. We'll be in touch within 24 hours.");
      setForm({ name: "", email: "", phone: "", project_type: "", message: "" });
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.error(err);
      }
      toast.error("Something went wrong. Please call us directly at 636-849-6681.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-16 lg:py-24 bg-white"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left — info */}
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-orange-600 mb-4">
              <span className="w-1.5 h-1.5 bg-orange-500 rounded-full" />
              GET A FREE QUOTE
            </div>
            <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.025em] text-stone-900">
              Let's design
              <br />
              <span className="text-stone-500">your project.</span>
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-stone-600 max-w-md">
              We respond within one business day. No pushy follow-ups, no
              automated funnels — just an honest conversation.
            </p>

            <div className="mt-10 space-y-3">
              <a
                href="tel:+16368496681"
                data-testid="contact-phone"
                className="flex items-center gap-4 bg-stone-50 hover:bg-stone-100 p-5 rounded-2xl border border-stone-200 group transition-colors"
              >
                <div className="w-12 h-12 bg-stone-900 group-hover:bg-orange-600 rounded-full flex items-center justify-center text-stone-50 transition-colors">
                  <Phone size={18} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-stone-500 font-medium">
                    Call us · Open 24/7
                  </div>
                  <div className="mt-1 font-display font-semibold text-2xl text-stone-900">
                    636-849-6681
                  </div>
                </div>
              </a>

              <a
                href="mailto:mlexteriordesign@gmail.com"
                data-testid="contact-email"
                className="flex items-center gap-4 bg-stone-50 hover:bg-stone-100 p-5 rounded-2xl border border-stone-200 group transition-colors"
              >
                <div className="w-12 h-12 bg-stone-900 group-hover:bg-orange-600 rounded-full flex items-center justify-center text-stone-50 transition-colors">
                  <Mail size={18} />
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wider text-stone-500 font-medium">
                    Email
                  </div>
                  <div className="mt-1 font-semibold text-stone-900 break-all">
                    mlexteriordesign@gmail.com
                  </div>
                </div>
              </a>

              <div className="flex items-center gap-4 bg-stone-50 p-5 rounded-2xl border border-stone-200">
                <div className="w-12 h-12 bg-white border border-stone-300 rounded-full flex items-center justify-center text-stone-900">
                  <MapPin size={18} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-stone-500 font-medium">
                    Service area
                  </div>
                  <div className="mt-1 font-semibold text-stone-900">
                    Greater St. Louis · St. Charles County
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="quote-form"
              className="bg-stone-50 border border-stone-200 rounded-3xl p-8 md:p-10 space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-stone-800 mb-2">
                    Your name *
                  </label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={update("name")}
                    data-testid="input-name"
                    placeholder="Jane Doe"
                    className="w-full bg-white border border-stone-300 rounded-xl px-4 py-3 text-stone-900 placeholder:text-stone-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 focus:outline-none transition"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-stone-800 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    data-testid="input-email"
                    placeholder="jane@example.com"
                    className="w-full bg-white border border-stone-300 rounded-xl px-4 py-3 text-stone-900 placeholder:text-stone-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 focus:outline-none transition"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-stone-800 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    data-testid="input-phone"
                    placeholder="(636) 555-0100"
                    className="w-full bg-white border border-stone-300 rounded-xl px-4 py-3 text-stone-900 placeholder:text-stone-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 focus:outline-none transition"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-stone-800 mb-2">
                    Project type *
                  </label>
                  <Select
                    value={form.project_type}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, project_type: v }))
                    }
                  >
                    <SelectTrigger
                      data-testid="select-project-type"
                      className="w-full bg-white border border-stone-300 rounded-xl px-4 py-3 h-auto text-stone-900 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 data-[placeholder]:text-stone-400"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROJECT_TYPES.map((p) => (
                        <SelectItem
                          key={p.value}
                          value={p.value}
                          data-testid={`option-${p.value}`}
                        >
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-stone-800 mb-2">
                  Tell us about your project
                </label>
                <textarea
                  value={form.message}
                  onChange={update("message")}
                  data-testid="input-message"
                  rows={4}
                  placeholder="Approximate size, materials you like, timeline, budget range…"
                  className="w-full bg-white border border-stone-300 rounded-xl px-4 py-3 text-stone-900 placeholder:text-stone-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 focus:outline-none transition resize-none"
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                <p className="text-xs text-stone-500 max-w-xs">
                  We respond within 24 hours · Available 7 days a week.
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-quote"
                  className="group inline-flex items-center justify-center gap-2 px-7 py-4 bg-stone-900 text-stone-50 font-semibold rounded-full hover:bg-orange-600 transition-colors disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      Send Request
                      <ArrowRight
                        size={18}
                        className="group-hover:translate-x-1 transition-transform"
                      />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
